import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "wouter";
import { ArrowLeft, CheckCircle2, LifeBuoy, Loader2, Send, Ticket } from "lucide-react";
import { AppLayout } from "@/components/app-layout";
import { BrandLogo } from "@/components/brand-logo";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/components/language-provider";
import { useToast } from "@/hooks/use-toast";
import { getApiUrl } from "@/lib/config";

export interface TicketSummary {
  number: number;
  code: string;
  subject: string;
  status: "open" | "answered" | "closed";
  channel: string;
  createdAt: string;
  updatedAt: string;
  closedAt?: string | null;
}

interface TicketMessage { id: string; author: "customer" | "staff" | "system"; authorName?: string | null; body: string; attachmentName?: string | null; createdAt: string }
interface TicketDetail extends TicketSummary { requesterName: string; messages: TicketMessage[] }

const TEXTS = {
  pt: {
    status: { open: "Aguardando a FillSign", answered: "Respondido: aguardando você", closed: "Fechado" },
    channel: { app: "Ajuda do app", chat: "Chat do app", whatsapp: "WhatsApp", site: "Site" } as Record<string, string>,
    opened: (d: string) => `Aberto em ${d}`,
    you: "Você",
    team: "Equipe FillSign",
    reply: "Escrever uma resposta",
    replyClosed: "Escrever de novo reabre o chamado.",
    send: "Enviar",
    close: "Fechar chamado",
    closedNote: "Chamado fechado. Se precisar, escreva de novo que ele reabre.",
    sent: "Mensagem enviada. A equipe recebe na hora.",
    tooMany: "Muitas mensagens neste chamado. Tente de novo em uma hora.",
    failed: "Não deu certo agora. Tente de novo.",
    notFound: "Chamado não encontrado.",
    notFoundPublic: "Este link não abre o chamado. Abra o link exatamente como veio no e-mail ou no WhatsApp.",
    back: "Voltar para a Ajuda",
    mine: "Meus chamados",
    mineDesc: "Cada pedido tem um número. Acompanhe aqui as respostas da equipe.",
    none: "Nenhum chamado ainda.",
    updated: (d: string) => `Atualizado em ${d}`,
    keep: "Guarde este número: é com ele que você e a equipe acompanham o pedido.",
  },
  en: {
    status: { open: "Waiting for FillSign", answered: "Answered: waiting for you", closed: "Closed" },
    channel: { app: "App help", chat: "App chat", whatsapp: "WhatsApp", site: "Website" } as Record<string, string>,
    opened: (d: string) => `Opened on ${d}`,
    you: "You",
    team: "FillSign team",
    reply: "Write an answer",
    replyClosed: "Writing again reopens the request.",
    send: "Send",
    close: "Close request",
    closedNote: "Request closed. If you need to, write again and it reopens.",
    sent: "Message sent. The team gets it at once.",
    tooMany: "Too many messages in this request. Try again in an hour.",
    failed: "That did not work now. Try again.",
    notFound: "Request not found.",
    notFoundPublic: "This link does not open the request. Open the link exactly as it came in the e-mail or on WhatsApp.",
    back: "Back to Help",
    mine: "My requests",
    mineDesc: "Each request has a number. Follow the team's answers here.",
    none: "No requests yet.",
    updated: (d: string) => `Updated on ${d}`,
    keep: "Keep this number: it is how you and the team follow the request.",
  },
};

function useTexts() {
  const { language } = useLanguage();
  const lang = language === "en" ? "en" : "pt";
  return { tx: TEXTS[lang], locale: lang === "en" ? "en-US" : "pt-BR" };
}

export function TicketStatusBadge({ status }: { status: TicketSummary["status"] }) {
  const { tx } = useTexts();
  const cls = status === "answered" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
    : status === "open" ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300" : "text-muted-foreground";
  return <Badge variant="outline" className={cls} data-testid={`ticket-status-${status}`}>{tx.status[status]}</Badge>;
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** The thread with the reply box; the same for the app page and the public link page. */
function TicketThread({ ticket, onReply, onClose }: { ticket: TicketDetail; onReply: (body: string) => Promise<"ok" | "limit" | "error">; onClose?: () => Promise<void> }) {
  const { tx, locale } = useTexts();
  const { toast } = useToast();
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const when = (d: string) => new Date(d).toLocaleString(locale, { dateStyle: "short", timeStyle: "short" });

  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    const r = await onReply(body.trim());
    setBusy(false);
    if (r === "ok") { setBody(""); toast({ title: tx.sent }); }
    else toast({ title: r === "limit" ? tx.tooMany : tx.failed, variant: "destructive" });
  };

  return (
    <Card data-testid="ticket-thread">
      <CardHeader className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2 py-0.5 font-mono text-sm font-semibold text-primary" data-testid="ticket-code"><Ticket className="h-3.5 w-3.5" />{ticket.code}</span>
          <TicketStatusBadge status={ticket.status} />
          <span className="text-xs text-muted-foreground">{tx.channel[ticket.channel] ?? ticket.channel} · {tx.opened(when(ticket.createdAt))}</span>
        </div>
        <CardTitle className="text-lg">{ticket.subject}</CardTitle>
        <CardDescription>{tx.keep}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ol className="space-y-3">
          {ticket.messages.map((m) => m.author === "system" ? (
            <li key={m.id} className="text-center text-xs text-muted-foreground">{m.body} · {when(m.createdAt)}</li>
          ) : (
            <li key={m.id} className={`rounded-lg border p-3 ${m.author === "staff" ? "border-primary/30 bg-primary/5" : "bg-muted/30"}`} data-testid={`ticket-message-${m.author}`}>
              <div className="mb-1 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{m.author === "staff" ? `${tx.team}${m.authorName ? ` (${m.authorName})` : ""}` : tx.you}</span>
                <span>{when(m.createdAt)}</span>
              </div>
              <p className="whitespace-pre-wrap break-words text-sm">{m.body}</p>
              {m.attachmentName && <p className="mt-1 text-xs text-muted-foreground">📎 {m.attachmentName}</p>}
            </li>
          ))}
        </ol>
        {ticket.status === "closed" && <p className="flex items-center gap-2 rounded-md bg-muted/50 p-2 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4" />{tx.closedNote}</p>}
        <div className="space-y-2">
          <Textarea rows={4} maxLength={8000} value={body} onChange={(e) => setBody(e.target.value)} placeholder={tx.reply} aria-label={tx.reply} data-testid="input-ticket-reply" />
          {ticket.status === "closed" && <p className="text-xs text-muted-foreground">{tx.replyClosed}</p>}
          <div className="flex flex-wrap justify-between gap-2">
            {onClose && ticket.status !== "closed" ? (
              <Button variant="ghost" size="sm" onClick={() => void onClose()} data-testid="button-close-ticket">{tx.close}</Button>
            ) : <span />}
            <Button onClick={() => void send()} disabled={busy || !body.trim()} data-testid="button-send-ticket-reply">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}{tx.send}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** /ajuda/chamados/:number (signed in). */
export function SupportTicketPage() {
  const { tx } = useTexts();
  const { number } = useParams<{ number: string }>();
  const [, setLocation] = useLocation();
  const [user, setUser] = useState<any>(null);
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (!stored) { setLocation(`/login?redirect=${encodeURIComponent(window.location.pathname)}`); return; }
    setUser(JSON.parse(stored));
  }, [setLocation]);

  const load = useCallback(async () => {
    const res = await fetch(getApiUrl(`/api/support/tickets/${number}`), { headers: authHeaders() });
    if (!res.ok) { setMissing(true); return; }
    setTicket(await res.json());
  }, [number]);
  useEffect(() => { if (user) void load(); }, [user, load]);

  const reply = async (body: string) => {
    const res = await fetch(getApiUrl(`/api/support/tickets/${number}/messages`), { method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: JSON.stringify({ body }) });
    if (res.status === 429) return "limit" as const;
    if (!res.ok) return "error" as const;
    setTicket(await res.json());
    return "ok" as const;
  };
  const close = async () => {
    const res = await fetch(getApiUrl(`/api/support/tickets/${number}/close`), { method: "POST", headers: authHeaders() });
    if (res.ok) setTicket(await res.json());
  };

  if (!user) return null;
  return (
    <AppLayout user={user} onLogout={() => { localStorage.removeItem("token"); localStorage.removeItem("user"); setLocation("/"); }} plan={user.plan} currentPage="/help">
      <div className="mx-auto max-w-2xl space-y-4">
        <Link href="/help"><Button variant="ghost" size="sm"><ArrowLeft className="mr-2 h-4 w-4" />{tx.back}</Button></Link>
        {missing ? <Card><CardContent className="py-10 text-center text-muted-foreground">{tx.notFound}</CardContent></Card>
          : ticket ? <TicketThread ticket={ticket} onReply={reply} onClose={close} />
          : <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
      </div>
    </AppLayout>
  );
}

/** /chamado/:number?t=...: the link of the e-mail or WhatsApp, without login. */
export function PublicSupportTicketPage() {
  const { tx } = useTexts();
  const { number } = useParams<{ number: string }>();
  const token = new URLSearchParams(window.location.search).get("t") ?? "";
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [missing, setMissing] = useState(false);
  const qs = `?t=${encodeURIComponent(token)}`;

  useEffect(() => {
    fetch(getApiUrl(`/api/support/public/tickets/${number}${qs}`))
      .then(async (r) => (r.ok ? setTicket(await r.json()) : setMissing(true)))
      .catch(() => setMissing(true));
  }, [number, qs]);

  const reply = async (body: string) => {
    const res = await fetch(getApiUrl(`/api/support/public/tickets/${number}/messages${qs}`), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ body }) });
    if (res.status === 429) return "limit" as const;
    if (!res.ok) return "error" as const;
    setTicket(await res.json());
    return "ok" as const;
  };

  return (
    <div className="min-h-screen bg-muted/30 px-4 py-8">
      <div className="mx-auto max-w-2xl space-y-4">
        <div className="flex justify-center pb-2"><BrandLogo /></div>
        {missing ? <Card><CardContent className="py-10 text-center text-muted-foreground">{tx.notFoundPublic}</CardContent></Card>
          : ticket ? <TicketThread ticket={ticket} onReply={reply} />
          : <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
      </div>
    </div>
  );
}

/** Ajuda: the person's tickets, newest first. */
export function MyTicketsCard({ refreshKey }: { refreshKey?: number }) {
  const { tx, locale } = useTexts();
  const [tickets, setTickets] = useState<TicketSummary[] | null>(null);
  useEffect(() => {
    fetch(getApiUrl("/api/support/tickets"), { headers: authHeaders() })
      .then(async (r) => setTickets(r.ok ? await r.json() : []))
      .catch(() => setTickets([]));
  }, [refreshKey]);

  return (
    <Card data-testid="card-my-tickets">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><LifeBuoy className="h-5 w-5" />{tx.mine}</CardTitle>
        <CardDescription>{tx.mineDesc}</CardDescription>
      </CardHeader>
      <CardContent>
        {tickets === null ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          : tickets.length === 0 ? <p className="text-sm text-muted-foreground">{tx.none}</p>
          : (
            <ul className="divide-y rounded-lg border">
              {tickets.map((t) => (
                <li key={t.number}>
                  <Link href={`/ajuda/chamados/${t.number}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 p-3 transition hover:bg-muted/40" data-testid={`my-ticket-${t.number}`}>
                    <span className="font-mono text-sm font-semibold text-primary">{t.code}</span>
                    <span className="min-w-0 flex-1 truncate text-sm">{t.subject}</span>
                    <TicketStatusBadge status={t.status} />
                    <span className="w-full text-xs text-muted-foreground sm:w-auto">{tx.updated(new Date(t.updatedAt).toLocaleDateString(locale))}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
      </CardContent>
    </Card>
  );
}
